import {ReactElement, useEffect, useMemo, useRef, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Body as TableBody, Cell, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {
    useCalculateCurveExpirationsLazyQuery,
    useGetExpirationCurveQuery,
    useMassUpdateLeaseExpirationCountsForPropertyAndPeriodMutation,
    useSetExpirationCurveMutation,
    VersionType
} from "../../../../../__generated__/generated_types";
import * as css from "./leaseExpirationCurveModal.module.scss";
import LeaseExpirationCurveChart from "../../../../bulk-assumptions/components/lease-expiration-curve/LeaseExpirationCurve";
import {Property} from "../../../../../contexts/properties/logic";
import {adjustValuesTo100} from "../../../../bulk-assumptions/BulkAssumptionMathHelpers";
import {getMonths} from "../../../../../utils/months-helper";
import {toast} from "react-toastify";

interface UnitTypeExpirations {
    unitTypeId: string;
    unitTypeName: string;
    reforecastExpirations: number[];
    budgetExpirations: number[];
}

export interface LeaseExpirationCurveModalProps {
    property: Property;
    budgetYear: number;
    handleCancel: () => void;
    handleConfirm: () => void;
}

export function LeaseExpirationCurveModal(props: LeaseExpirationCurveModalProps): ReactElement {
    const setCurveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [expirationCurve, setExpirationCurve] = useState<number[] | null>(null);
    const [goodExpirationCurve, setGoodExpirationCurve] = useState<number[] | null>(null);
    const [is100Percent, setIs100Percent] = useState<boolean>(false);
    const [expirationCounts, setExpirationCounts] = useState<UnitTypeExpirations[] | null>(null);

    const {data: curveData, loading: curveLoading} = useGetExpirationCurveQuery({
        fetchPolicy: "no-cache",
        variables: {
            propertyId: props.property.id,
            budgetYear: props.budgetYear,
        }
    });
    const [calculateExpirations, {data: expirationsData, loading: expirationsLoading}] = useCalculateCurveExpirationsLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [saveExpirationCurve, {loading: curveUpdateLoading}] = useSetExpirationCurveMutation();
    const [saveExpirationCounts, {loading: countsLoading}] = useMassUpdateLeaseExpirationCountsForPropertyAndPeriodMutation();

        const months = useMemo(() => getMonths(props.property), [props.property]);
    const chartReady = useMemo(() => {
        return curveData && !curveLoading && expirationCurve;
    }, [curveData, curveLoading, expirationCurve]);

    useEffect(() => {
        return () => {
            if (setCurveTimeout.current !== null) {
                clearTimeout(setCurveTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        if(curveLoading && !(curveData?.expirationCurve)) {
            return;
        }

        const curve = curveData?.expirationCurve;
        if(curve) {
            setExpirationCurve(curve.curve.map(x => Number(x ?? 0)));
        } else {
            setExpirationCurve(Array(12).fill(0));
        }
    }, [curveData, curveLoading]);

    useEffect(() => {
        if (!expirationCurve) return;

        const sum = expirationCurve.sum();
        if(Math.abs(100 - sum) < 1) {
            const goodCurve = adjustValuesTo100(expirationCurve);
            setIs100Percent(true);
            setGoodExpirationCurve(goodCurve);
        } else {
            setIs100Percent(false);
        }
    }, [expirationCurve]);

    useEffect(() => {
        if(!is100Percent || !goodExpirationCurve) {
            return;
        }

        calculateExpirations({
            variables: {
                propertyId: props.property.id,
                budgetYear: props.budgetYear,
                curve: goodExpirationCurve.map(x => String(x)),
            }
        });
    }, [is100Percent, goodExpirationCurve]);

    useEffect(() => {
        if(expirationsLoading && !(expirationsData?.calculateExpirationCurveExpirations)) {
            return;
        }

        if(expirationsData?.calculateExpirationCurveExpirations) {
            const result: UnitTypeExpirations[] = [];
            for (const utData of expirationsData.calculateExpirationCurveExpirations) {
                result.push({
                    unitTypeId: utData.unitTypeId,
                    unitTypeName: utData.unitTypeName,
                    reforecastExpirations: utData.reforecastExpirations,
                    budgetExpirations: utData.budgetExpirations
                });
            }
            setExpirationCounts(result);
        }
    }, [expirationsData, expirationsLoading]);

    function saveCurve(curveToSave: number[] | null): void {
        if (!curveToSave) {
            return;
        }
        saveExpirationCurve({
            variables: {
                propertyId: props.property.id,
                budgetYear: props.budgetYear,
                curve: curveToSave.map(x => String(x ?? 0)),
            }
        }).then((data) => {
            if (data.data?.setExpirationCurve) {
                toast.success("Curve template saved");
            } else {
                toast.error("There was a problem saving, please refresh and try again");
            }
        });
    }

    async function saveAll(curveToSave: number[] | null, countsToSave: UnitTypeExpirations[] | null): Promise<void> {
        if(!curveToSave || !countsToSave) {
            return;
        }

        const curvePromise = saveExpirationCurve({
            variables: {
                propertyId: props.property.id,
                budgetYear: props.budgetYear,
                curve: curveToSave.map(x => String(x ?? 0)),
            }
        });
        const countsPromise = saveExpirationCounts({
            variables: {
                propertyId: props.property.id,
                versionType: VersionType.Budget,
                updates: countsToSave.map(c => ({
                    unitTypeId: c.unitTypeId,
                    values: c.budgetExpirations
                })),
                budgetYear: props.budgetYear
            }
        });

        try {
            const [curveResult, countsResult] = await Promise.all([curvePromise, countsPromise]);

            if (curveResult.errors || countsResult.errors) {
                toast.error("There was a problem saving, please refresh and try again");
                return;
            }

            props.handleConfirm();
            toast.success("Curve and expiration counts updated");
        } catch {
            toast.error("There was a problem saving, please refresh and try again");
        }
    }

    function renderExpirationCountsTable(expirationCounts: UnitTypeExpirations[]): ReactElement {

        const sortedCounts = expirationCounts.sortBy(c => c.unitTypeName);
        const budgetLastTwoDigits = props.budgetYear - (Math.floor(props.budgetYear / 100) * 100);

        const rows:ReactElement[] = [];

        for(const counts of sortedCounts) {
            const row = <Row>
                <Cell colSpan={2} className={css.curveCountTableUnitType}>{counts.unitTypeName}</Cell>
                {Array(12).fill(null).map((_, i) => <Cell className={css.curveCountTableNumber}>{counts.budgetExpirations[i]}</Cell>)}
                <Cell className={css.curveCountTableNumber}>100%</Cell>
            </Row>;
            rows.push(row);
        }

        return <Table>
            <HeaderRow>
                <HeaderCell className={css.curveCountTableUnitType}>Unit Type</HeaderCell>
                <HeaderCell></HeaderCell>
                {months.map(m => <HeaderCell className={css.curveCountTableHeader}>{m} '{budgetLastTwoDigits}</HeaderCell>)}
                <HeaderCell className={css.curveCountTableHeader}>Unit Percentage</HeaderCell>
            </HeaderRow>
            <TableBody>
                {rows}
            </TableBody>
        </Table>;
    }

    function debounceSetExpirationCurve(values: number[]) {
        if (setCurveTimeout.current !== null) {
            clearTimeout(setCurveTimeout.current);
        }
        setCurveTimeout.current = setTimeout(() => {
            setExpirationCurve(values);
        }, 300);
    }

    return <Modal isLarge className={css.curveModal} onClose={props.handleCancel}>
        <Header>Lease Expiration Curve</Header>
        <Body className={css.curveModalBody}>
            <h5 className={css.curveChartHeader}>Create, update, or apply an expiration curve template.</h5>
            <p className={css.curveChartDescription}>The percentages for each month will be used to calculate the number of expirations for each Unit Type. Applying the curve will write over your current expiration counts for {props.budgetYear}.</p>
            {chartReady &&
                <LeaseExpirationCurveChart
                    versionType={VersionType.Budget}
                    existingValues={expirationCurve}
                    currentProperty={props.property}
                    onValueChange={(curveValues) => debounceSetExpirationCurve(curveValues)}
                />
            }

            <h5 className={css.curveSmallHeader}>Expiration Counts for Curve</h5>
            {!is100Percent ?
                <div className={css.curveCalculationErrorBox}><span>Expiration percentages must add up to 100% to preview or save expiration counts.</span></div> :
                !expirationCounts ?
                        <div>Unable to calculate expirations</div> :
                        renderExpirationCountsTable(expirationCounts)
            }
        </Body>
        <Footer>
            <FooterItem>
                <Button
                        isNeutral
                        disabled={!is100Percent || !goodExpirationCurve || curveUpdateLoading || countsLoading}
                        onClick={() => saveCurve(goodExpirationCurve)}>Save Curve Template</Button>
            </FooterItem>
            <FooterItem>
                <Button
                        isPrimary
                        disabled={!is100Percent || curveUpdateLoading || countsLoading}
                        onClick={() => saveAll(goodExpirationCurve, expirationCounts)}>Save & Apply Curve Template</Button>
            </FooterItem>
        </Footer>
        <Close aria-label="Close modal" />
    </Modal>;
}
